<script setup lang="ts">
import type {
  CrashDistributionBucket,
  CrashDistributionSlice,
} from '~/types/crash-pre-game';

type Props = {
  distribution: CrashDistributionSlice[];
};

const { distribution } = defineProps<Props>();

// O core manda a faixa como chave curta ("10-50x"); a tela fala como jogador.
// O mapa é fechado: faixa nova do core aparece com a chave crua em vez de
// sumir da tela.
const BUCKET_LABELS: Record<CrashDistributionBucket, string> = {
  '<2x': 'Menos de 2X',
  '2-5x': 'De 2X a 5X',
  '5-10x': 'De 5X a 10X',
  '10-50x': 'De 10X a 50X',
  '50x+': 'Mais de 50X',
};

// Degradê de frio (azul) pra quente (rosa) acompanhando a faixa: a cor sozinha
// já diz que a barra de baixo é a rodada rara.
const BUCKET_COLORS: Record<CrashDistributionBucket, string> = {
  '<2x': '#37c6f0',
  '2-5x': '#8f5be6',
  '5-10x': '#a560f5',
  '10-50x': '#ec5a98',
  '50x+': '#ff8fbe',
};

// Cada barra é proporcional à MAIOR faixa, não ao total: com "<2x" levando 47
// de 90, medir pelo total deixaria as outras quatro como riscos iguais.
const rows = computed(() => {
  const top = distribution.reduce(
    (acc, slice) => Math.max(acc, slice.count),
    0,
  );

  return distribution.map((slice) => ({
    ...slice,
    label: BUCKET_LABELS[slice.bucket] ?? slice.bucket,
    color: BUCKET_COLORS[slice.bucket] ?? '#37c6f0',
    width: top > 0 ? (slice.count / top) * 100 : 0,
  }));
});

function formatTimes(count: number): string {
  return `${count} ${count === 1 ? 'vez' : 'vezes'}`;
}
</script>

<template>
  <div class="crash-hist">
    <div
      v-for="(row, index) in rows"
      :key="row.bucket"
      class="crash-hist__row"
    >
      <span class="crash-hist__label">{{ row.label }}</span>

      <span class="crash-hist__track">
        <span
          class="crash-hist__bar"
          :class="{ 'crash-hist__bar--seen': row.count > 0 }"
          :style="{
            width: `${row.width}%`,
            background: row.color,
            animationDelay: `${index * 70}ms`,
          }"
        />
      </span>

      <span class="crash-hist__value">{{ formatTimes(row.count) }}</span>
    </div>
  </div>
</template>

<style scoped>
.crash-hist {
  display: flex;
  flex-direction: column;
}

/* No mobile a linha quebra: rótulo e contagem em cima, barra ocupando a
   largura inteira embaixo. No desktop cabem as três colunas lado a lado. Mesmo
   DOM nos dois — só as áreas do grid mudam. */
.crash-hist__row {
  display: grid;
  grid-template-areas:
    'label value'
    'track track';
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 7px 10px;
  padding: 10px 0;
  border-top: 1px solid #161f35;
}

.crash-hist__row:first-child {
  border-top: 0;
}

.crash-hist__label {
  grid-area: label;
  color: #e9eef9;
  font-size: 13px;
}

.crash-hist__value {
  grid-area: value;
  color: #e9eef9;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.crash-hist__track {
  grid-area: track;
  display: block;
  height: 16px;
  border-radius: 12px;
  background: #161f35;
  overflow: hidden;
}

.crash-hist__bar {
  display: block;
  height: 100%;
  border-radius: 12px;
  /* Cresce na entrada por @keyframes — `transition` está zerado no app todo. */
  transform-origin: left center;
  /* `backwards`, NÃO `both`: o fill pra frente deixava um transform residual
     (matrix identidade) grudado em toda barra depois da entrada, promovendo
     cada uma a camada composta dentro de uma track com overflow:hidden +
     border-radius — clip arredondado sobre camada transformada, que o Chromium
     às vezes deixa de pintar. A barra ficava vazia com a contagem certa ao
     lado. Como o `to` é scaleX(1), o estado natural do elemento, descartar o
     valor animado no fim não muda nada na tela; o `backwards` segue segurando
     o scaleX(0) durante o animation-delay de cada faixa. */
  animation: crash-hist-in 800ms cubic-bezier(0.22, 0.9, 0.3, 1) backwards;
}

/* Cada barra é proporcional à MAIOR faixa, e a "<2x" domina sempre: uma faixa
   rara com 1 ocorrência em 40 dá ~2% da track, que com o raio de 12px não
   desenha nada. O piso garante um traço legível pra toda faixa que aconteceu —
   faixa com 0 continua vazia, que é o que ela tem a dizer. */
.crash-hist__bar--seen {
  min-width: 10px;
}

@media (min-width: 1024px) {
  .crash-hist__row {
    grid-template-areas: 'label track value';
    grid-template-columns: 150px minmax(0, 1fr) 66px;
    align-items: center;
    gap: 14px;
    padding: 9px 0;
  }

  .crash-hist__track {
    height: 18px;
  }
}

@keyframes crash-hist-in {
  from {
    transform: scaleX(0);
  }

  to {
    transform: scaleX(1);
  }
}

@media (prefers-reduced-motion: reduce) {
  .crash-hist__bar {
    animation: none;
  }
}
</style>
